import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Flex, Modal, Text } from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { formatDate, formatMs, STEP_STATE_LABELS } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

const DRILLDOWN_TABS = [
  { key: 'all', label: t.tutteEsecuzioni, icon: 'pi-list' },
  { key: 'errors', label: t.errori, icon: 'pi-exclamation-triangle' }
]

export const DrillDown = ({ unit, startDate, endDate, onClose }) => {
  const [activeTab, setActiveTab] = useState('all')
  const [showSlowOnly, setShowSlowOnly] = useState(false)
  const navigate = useNavigate()

  const { data: stats = [] } = useCQuery(['unitPerformanceStats', startDate, endDate])
  const unitStats = stats.find((s) => s.unit === unit) || {}
  const p90 = unitStats.p90 || 0

  const { data: executions = [], isSuccess } = useCQuery([
    'unitPerformanceDrillDown',
    unit,
    startDate,
    endDate,
    showSlowOnly && activeTab === 'all' && p90 > 0 ? p90 : undefined,
    undefined
  ])

  const { data: errorExecs = [], isSuccess: errorsLoaded } = useCQuery([
    'unitPerformanceDrillDown',
    unit,
    startDate,
    endDate,
    undefined,
    5
  ])

  const displayData = activeTab === 'errors' ? errorExecs : executions
  const isDataReady = activeTab === 'errors' ? errorsLoaded : isSuccess

  if (!unit) return null

  return (
    <Modal
      visible
      onHide={onClose}
      header={`${t.dettaglio} ${unit}`}
      className="nora-dialog"
      contentStyle={{ padding: 0 }}
      style={{ width: '80vw', maxWidth: 1000 }}
    >
      <div
        className="nora-dialog__body--compact"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <div style={{ display: 'flex', gap: 16 }}>
          <Flex>
            <Text value={t.media} size={11} style={{ color: 'var(--ink-500)' }} />
            <Text bold value={formatMs(unitStats.avg)} size={16} />
          </Flex>
          <Flex>
            <Text value="P90" size={11} style={{ color: 'var(--ink-500)' }} />
            <Text bold value={formatMs(unitStats.p90)} size={16} />
          </Flex>
          <Flex>
            <Text value="P95" size={11} style={{ color: 'var(--ink-500)' }} />
            <Text bold value={formatMs(unitStats.p95)} size={16} />
          </Flex>
          <Flex>
            <Text value={t.errori} size={11} style={{ color: 'var(--ink-500)' }} />
            <Text
              bold
              value={`${unitStats.errorCount || 0} (${unitStats.errorRate || 0}%)`}
              size={16}
              style={{ color: unitStats.errorRate > 5 ? 'var(--danger)' : undefined }}
            />
          </Flex>
        </div>
      </div>

      <div className="nora-dialog__tabs" style={{ borderBottom: '1px solid var(--border)' }}>
        {DRILLDOWN_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`nora-dialog__tab ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`pi ${tab.icon}`} style={{ fontSize: 12 }} />
            {tab.label}
            {tab.key === 'errors' && (
              <span className="nbadge nbadge--err" style={{ marginLeft: 6 }}>
                {errorExecs.length}
              </span>
            )}
          </button>
        ))}
        {activeTab === 'all' && (
          <button
            type="button"
            className="nbtn nbtn--sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => setShowSlowOnly(!showSlowOnly)}
          >
            {showSlowOnly ? t.mostraTutti : `${t.soloLenti} (>${formatMs(p90)})`}
          </button>
        )}
      </div>

      <div className="nora-dialog__body" style={{ padding: 0 }}>
        {isDataReady && (
          <DataTable
            value={displayData}
            size="small"
            scrollable
            scrollHeight="400px"
            sortMode="single"
            removableSort
            emptyMessage={
              activeTab === 'errors' ? t.nessunErroreTrovato : t.nessunaEsecuzioneTrovata
            }
            onRowClick={(e) => navigate(`/process/stats/${e.data.processId}`)}
            rowClassName={(row) => (row.stepState === 5 ? 'row-error' : '')}
            rowHover
          >
            <Column
              field="executionTime"
              header={t.tempo}
              sortable
              style={{ width: 100 }}
              body={(row) => (
                <Text
                  bold
                  value={formatMs(row.executionTime)}
                  size={13}
                  style={
                    row.executionTime > (unitStats.p95 || Infinity)
                      ? { color: 'var(--danger)' }
                      : {}
                  }
                />
              )}
            />
            <Column
              field="stepState"
              header={t.state}
              style={{ width: 100 }}
              body={(row) => (
                <Text
                  value={STEP_STATE_LABELS[row.stepState] || row.stepState}
                  size={13}
                  style={{ color: row.stepState === 5 ? 'var(--danger)' : 'var(--success)' }}
                />
              )}
            />
            <Column
              field="startTime"
              header={t.inizio}
              sortable
              style={{ width: 130 }}
              body={(row) => <Text value={formatDate(row.startTime)} size={13} />}
            />
            <Column
              field="processId"
              header={t.processo}
              sortable
              style={{ width: 80 }}
              body={(row) => (
                <Text
                  value={`#${row.processId}`}
                  size={13}
                  style={{ color: 'var(--primary)', cursor: 'pointer' }}
                />
              )}
            />
            <Column
              field="conversationId"
              header={t.conv}
              style={{ width: 80 }}
              body={(row) => <Text value={row.conversationId || '-'} size={13} />}
            />
            <Column
              field="processName"
              header={t.nomeProcesso}
              style={{ minWidth: 150 }}
              body={(row) => <Text value={row.processName || '-'} size={13} />}
            />
          </DataTable>
        )}

        <style>{`
          .row-error { background: var(--danger-tint) !important; }
          .nora-dialog .p-datatable-tbody > tr { cursor: pointer; }
          .nora-dialog .p-datatable-tbody > tr:hover > td { background: var(--surface-2); }
        `}</style>
      </div>
    </Modal>
  )
}
